import { useCallback, useState } from "react";
import { useMyPermissions } from "../../lib/permissions.js";
import { denialOrRawMessage } from "../../lib/denialReason.js";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";
import {
    createPerplexityAuditIntegration,
    deletePerplexityAuditIntegration,
    rotatePerplexityAuditCredential,
} from "../../lib/perplexityAuditClient.js";
import { NO_DIRECTORY, perplexitySummary, perplexitySteps, scopeCaptureStep, whenLabel } from "./model.js";
import { AsideSection, SourceLoadState, SourcePage, Step, Steps } from "../../components/setupSteps.jsx";
import Button from "../../components/ui/Button.jsx";
import { CopyField } from "../../components/ui/Copy.jsx";
import Notice, { ErrorText } from "../../components/ui/Notice.jsx";

export default function PerplexityPage({ source, health, entry, directory, onBack, onChanged, onReplace }) {
    const caps = useMyPermissions();
    const summary = entry?.data;
    // The bearer token is only ever returned by create and rotate, and only
    // once; it lives in component state until the admin leaves the page.
    const [oneTimeToken, setOneTimeToken] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(false);

    const run = useCallback(
        async (action) => {
            setSaving(true);
            setError(null);
            try {
                const next = perplexitySummary(await action());
                setOneTimeToken(next.webhookToken || null);
                onReplace({ ...next, webhookToken: "" });
            } catch (err) {
                setError(denialOrRawMessage(err, caps));
            } finally {
                setSaving(false);
            }
        },
        [caps, onReplace],
    );

    const remove = useCallback(async () => {
        setConfirmRemove(false);
        setSaving(true);
        setError(null);
        try {
            await deletePerplexityAuditIntegration();
            setOneTimeToken(null);
            onReplace(perplexitySummary(null));
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [caps, onReplace]);

    if (!summary) {
        return (
            <SourcePage source={source} health={health} onBack={onBack} backLabel="Back to data sources" busy={!entry?.loaded || entry?.loading} aside={null}>
                <SourceLoadState entry={entry} onRetry={onChanged} />
            </SourcePage>
        );
    }

    const configured = summary.configured;
    const {
        states: [createState, consoleState],
        noDirectory: scopedOut,
    } = scopeCaptureStep(perplexitySteps(summary), directory);
    const noDirectory = scopedOut && !oneTimeToken;
    const previousUntil = whenLabel(summary.previousCredentialValidUntil);

    return (
        <SourcePage
            source={source}
            health={health}
            onBack={onBack}
            backLabel="Back to data sources"
            busy={entry.loading}
            aside={
                <>
                    <AsideSection title="What Origin does">
                        <p className="m-0">
                            Receives Perplexity prompts, responses, and automated agent actions through an
                            authenticated webhook.
                        </p>
                    </AsideSection>
                    {configured ? (
                        <>
                            <AsideSection title="Retention">
                                <p className="m-0">
                                    Authorized audit payloads are kept for {summary.rawRetentionDays} days.
                                </p>
                            </AsideSection>
                            <AsideSection title="Remove source">
                                <p className="m-0">
                                    Perplexity deliveries stop immediately. Audit events already received are
                                    kept.
                                </p>
                                <Button variant="danger" onClick={() => setConfirmRemove(true)} disabled={saving} className="w-fit">
                                    Remove
                                </Button>
                            </AsideSection>
                        </>
                    ) : null}
                </>
            }
        >
            <Steps label="Perplexity setup">
                <Step
                    number={1}
                    state={createState}
                    title="Create the webhook in Origin"
                    summary={
                        configured
                            ? "Done. Paste this URL into the Perplexity admin console."
                            : "Origin creates a webhook URL and a one-time bearer token for Perplexity."
                    }
                    action={
                        configured ? null : (
                            <Button variant="primary" onClick={() => run(createPerplexityAuditIntegration)} disabled={saving || entry.loading}>
                                {saving ? "Creating…" : "Create webhook"}
                            </Button>
                        )
                    }
                >
                    {configured && summary.webhookUrl ? (
                        <CopyField label="Webhook URL" value={summary.webhookUrl} />
                    ) : null}
                    {configured ? null : <ErrorText>{error}</ErrorText>}
                </Step>
                <Step
                    number={2}
                    state={oneTimeToken ? "current" : consoleState}
                    title={noDirectory ? NO_DIRECTORY.label : "Add the URL and bearer token in Perplexity"}
                    summary={
                        noDirectory
                            ? NO_DIRECTORY.detail
                            : !configured
                              ? "Starts after the webhook is created."
                              : consoleState === "failed" && !scopedOut
                                ? "Perplexity deliveries are failing. Check the URL and token in the Perplexity admin console, or rotate the token and update it there."
                                : oneTimeToken
                                  ? "In the Perplexity admin console, use the webhook URL above and send this token as the Authorization header: Bearer <token>."
                                  : "Perplexity sends audit events with the bearer token. Rotate it if it was exposed."
                    }
                    action={
                        configured ? (
                            <Button onClick={() => run(rotatePerplexityAuditCredential)} disabled={saving}>
                                {saving ? "Rotating…" : "Rotate token"}
                            </Button>
                        ) : null
                    }
                    last
                >
                    {oneTimeToken ? (
                        <Notice tone="warning" title="Copy this bearer token now. Origin will not show it again.">
                            <CopyField label="Bearer token" value={oneTimeToken} className="mt-[8px]" />
                        </Notice>
                    ) : null}
                    {previousUntil ? (
                        <p className="m-0 text-[12.5px] leading-[1.45] text-[color:var(--muted)]">
                            The previous token keeps working until {previousUntil}, so you can update Perplexity
                            without dropping events.
                        </p>
                    ) : null}
                    {configured ? <ErrorText>{error}</ErrorText> : null}
                </Step>
            </Steps>
            <ConfirmDialog
                open={confirmRemove}
                analyticsId={DIALOG.PERPLEXITY_WEBHOOK_REMOVE}
                eyebrow="Perplexity Audit Logs"
                title="Remove the Perplexity webhook?"
                body="Perplexity deliveries will stop immediately. Previously retained audit events are not deleted."
                confirmLabel="Remove webhook"
                destructive
                onConfirm={remove}
                onCancel={() => setConfirmRemove(false)}
            />
        </SourcePage>
    );
}
